/**
 * Sleep's own vocabulary, mapped onto the panel-wide design tokens in
 * component/common/designSystem/theme.js.
 */
import {
  CHART_COLORS,
  toneChip,
  toneColor,
  toneColors,
  toneLookup
} from '../../component/common/designSystem';

// Quality is an ordered scale, not a set of categories, so it gets a ramp
// (worst → best) rather than the categorical palette.
export const SLEEP_QUALITY_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.green,
  CHART_COLORS.greenDeep,
  CHART_COLORS.teal
];

// Where a log came from. Manual entry is the brand colour; wearable-synced
// keeps the same blue it has in every other split.
export const SLEEP_SOURCE_COLORS = toneColors(['brand', 'info', 'teal', 'warning']);

/**
 * Duration bands (Under 5h → 9h+). Ordered like quality, but it peaks in the
 * middle rather than at the end: 7–8h is the healthy band, and 9h+ is drawn
 * back in amber because oversleeping is a signal too, not a better score.
 */
export const SLEEP_DURATION_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green,
  CHART_COLORS.amber
];

// Score bands 0–2 → 8–10: a plain worst → best ramp.
export const SLEEP_SCORE_RAMP = [
  CHART_COLORS.red,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.green,
  CHART_COLORS.greenDeep
];

/**
 * Bedtime and wake-time buckets. Seven buckets outrun the five-colour
 * categorical palette, and they are a sequence rather than categories, so they
 * get their own dusk → dawn ramp instead.
 */
export const TIME_OF_DAY_RAMP = [
  CHART_COLORS.moss,
  CHART_COLORS.brand,
  CHART_COLORS.brandSoft,
  CHART_COLORS.blue,
  CHART_COLORS.teal,
  CHART_COLORS.orange,
  CHART_COLORS.amber
];

// How many nights of the last 30 a user logged: fewest → most.
export const CONSISTENCY_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.amber,
  CHART_COLORS.blue,
  CHART_COLORS.greenDeep
];

// Listening-session length and plays-per-listener share the same "shallow →
// deep engagement" reading, so they share a ramp.
export const ENGAGEMENT_RAMP = [
  CHART_COLORS.gray,
  CHART_COLORS.brandSoft,
  CHART_COLORS.brand,
  CHART_COLORS.greenDeep,
  CHART_COLORS.green
];

const qualityTone = toneLookup(
  {
    excellent: 'success',
    'very good': 'success',
    good: 'teal',
    okay: 'warning',
    poor: 'danger',
    bad: 'danger'
  },
  'danger'
);

export const qualityColor = (label) => toneColor(qualityTone(label));
export const qualityChipClass = (label) => toneChip(qualityTone(label));

// The 8-hour reference line drawn on every sleep-duration chart.
export const SLEEP_TARGET_MINS = 480;
export const sleepTargetAnnotation = {
  yaxis: [
    {
      y: SLEEP_TARGET_MINS,
      borderColor: CHART_COLORS.green,
      strokeDashArray: 6,
      label: {
        text: '8h recommended',
        style: { color: '#fff', background: CHART_COLORS.green }
      }
    }
  ]
};
